import { h } from 'preact'
import { useEffect, useMemo, useState } from 'preact/hooks'

import type { QaScenario, QaSitemapNode, QaStatusArtifact } from './types'
import type { QaStrings } from './i18n'
import { LaneIcons } from './LaneIcons'
import { normalizePattern } from './resolver'
import { botTriColor, formatQaDate, humanTriColor, QA_COLOR_MODIFIER, shortVersion } from './ui'

/**
 * Preact port of the donor `qa-sitemap-modal.vue`.
 *
 * Presentational apart from the Escape-key listener (the one place a hook is
 * warranted — the donor used a `watch` + `window.addEventListener`). Data comes
 * in as the already-fetched artifact; this component owns no store or fetching.
 *
 * The donor's `<script setup>` tree reconstruction is extracted as the pure
 * exported `buildTree` helper so it's unit-testable and reusable by the mount
 * task.
 */
export interface SitemapModalProps {
  artifact: QaStatusArtifact
  currentPattern: string | null
  strings: QaStrings
  onClose: () => void
}

/** A node of the rebuilt sitemap tree, flattened depth-first for rendering. The
 *  flat `artifact.sitemap` links children to parents via `parent_id`; we rebuild
 *  the hierarchy and emit `depth` + `hasChildren` (donor `flatTree`). */
export interface TreeRow {
  node: QaSitemapNode
  depth: number
  hasChildren: boolean
}

export function buildTree(nodes: ReadonlyArray<QaSitemapNode>): TreeRow[] {
  const childrenByParent = new Map<string | null, QaSitemapNode[]>()
  for (const node of nodes) {
    const bucket = childrenByParent.get(node.parent_id) ?? []
    bucket.push(node)
    childrenByParent.set(node.parent_id, bucket)
  }
  for (const bucket of childrenByParent.values()) {
    bucket.sort((a, b) => a.order - b.order)
  }
  const out: TreeRow[] = []
  const visit = (parentId: string | null, depth: number): void => {
    for (const node of childrenByParent.get(parentId) ?? []) {
      const children = childrenByParent.get(node.id) ?? []
      out.push({ node, depth, hasChildren: children.length > 0 })
      if (children.length > 0) {
        visit(node.id, depth + 1)
      }
    }
  }
  visit(null, 0)
  return out
}

interface CaseEntry {
  scenario: QaScenario
  primaries: Set<string>
  traversed: Set<string>
}

/** Dedupe scenarios across pages by `external_key ?? id` (donor `dedupedCases`):
 *  a cross-module/traversed case that appears on several pages lists ONCE, with
 *  its primary/traversed pattern sets merged. */
function dedupeScenarios(artifact: QaStatusArtifact): CaseEntry[] {
  const byKey = new Map<string, CaseEntry>()
  for (const page of artifact.pages) {
    for (const scenario of page.scenarios) {
      const key = scenario.external_key ?? scenario.id
      const entry = byKey.get(key) ?? { scenario, primaries: new Set<string>(), traversed: new Set<string>() }
      for (const ref of scenario.pages ?? []) {
        ;(ref.role === 'primary' ? entry.primaries : entry.traversed).add(ref.pattern)
      }
      byKey.set(key, entry)
    }
  }
  const rank = (p?: string): number => (p ? Number(p.slice(1)) : 9)
  return [...byKey.values()].sort((a, b) => {
    const ga = a.scenario.group?.key ?? '~'
    const gb = b.scenario.group?.key ?? '~'
    if (ga !== gb) {
      return ga.localeCompare(gb)
    }
    return rank(a.scenario.priority) - rank(b.scenario.priority) || a.scenario.title.localeCompare(b.scenario.title)
  })
}

function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? ''))
}

/** KPI formatting: the metric values are already whole percentages (0–100), as
 *  emitted by `qa build` (`cli/src/qa/build.ts` rounds `100*num/den`). Render
 *  them directly — do NOT multiply by 100, or a CLI artifact's `25` becomes
 *  "2500%". See the QaMetric contract in `types.ts`. */
function formatPct(pct: number): string {
  return `${Math.round(pct)}%`
}

export function SitemapModal({ artifact, currentPattern, strings, onClose }: SitemapModalProps) {
  const [activeTab, setActiveTab] = useState<'pages' | 'scenarios'>('pages')
  const [search, setSearch] = useState('')

  // The one place a hook is warranted: close on Escape, cleaned up on unmount.
  useEffect(() => {
    const onKeydown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', onKeydown)
    return () => window.removeEventListener('keydown', onKeydown)
  }, [onClose])

  const here = currentPattern ? normalizePattern(currentPattern) : null
  const tree = useMemo(() => buildTree(artifact.sitemap), [artifact])
  const cases = useMemo(() => dedupeScenarios(artifact), [artifact])

  const titleByPattern = useMemo(() => {
    const map = new Map<string, string>()
    for (const node of artifact.sitemap) {
      map.set(node.path_pattern, node.title)
    }
    return map
  }, [artifact])
  const patternTitle = (pattern: string): string => titleByPattern.get(pattern) ?? pattern

  const query = search.trim().toLowerCase()
  const filteredCases = query
    ? cases.filter(
        (c) =>
          c.scenario.title.toLowerCase().includes(query) ||
          (c.scenario.group?.label ?? '').toLowerCase().includes(query),
      )
    : cases

  const freshness = interpolate(strings['panel.freshness'], {
    date: formatQaDate(artifact.generated_at),
    version: shortVersion(artifact.app_version),
    env: artifact.environment,
  })

  return h(
    'div',
    {
      class: 'qa-modal__backdrop',
      'data-testid': 'qa-sitemap-modal',
      onClick: (e: MouseEvent) => {
        if (e.target === e.currentTarget) onClose()
      },
    },
    h(
      'div',
      { class: 'qa-modal', role: 'dialog', 'aria-modal': 'true' },
      h(
        'header',
        { class: 'qa-modal__head' },
        h('h3', null, strings['modal.title']),
        h(
          'button',
          {
            type: 'button',
            class: 'qa-modal__close',
            title: strings['modal.close'],
            'aria-label': strings['modal.close'],
            'data-testid': 'qa-modal-close',
            onClick: onClose,
          },
          '×',
        ),
      ),
      h('div', { class: 'qa-modal__freshness' }, freshness),
      // Dual metric (doc 01 D12) — rendered as rounded percentages.
      h(
        'div',
        { class: 'qa-kpi', 'data-testid': 'qa-kpi' },
        h(
          'div',
          { class: 'qa-kpi__item' },
          h('span', { class: 'qa-kpi__value' }, formatPct(artifact.metric.pages_with_scenarios_pct)),
          h('span', { class: 'qa-kpi__label' }, strings['modal.kpiPagesCovered']),
        ),
        h(
          'div',
          { class: 'qa-kpi__item' },
          h('span', { class: 'qa-kpi__value' }, formatPct(artifact.metric.scenarios_passing_pct)),
          h('span', { class: 'qa-kpi__label' }, strings['modal.kpiScenariosPassing']),
        ),
      ),
      h(
        'nav',
        { class: 'qa-tabs' },
        h(
          'button',
          {
            type: 'button',
            class: `qa-tabs__tab ${activeTab === 'pages' ? 'qa-tabs__tab--active' : ''}`,
            'data-testid': 'qa-tab-pages',
            onClick: () => setActiveTab('pages'),
          },
          strings['modal.tabPages'],
        ),
        h(
          'button',
          {
            type: 'button',
            class: `qa-tabs__tab ${activeTab === 'scenarios' ? 'qa-tabs__tab--active' : ''}`,
            'data-testid': 'qa-tab-scenarios',
            onClick: () => setActiveTab('scenarios'),
          },
          strings['modal.tabScenarios'],
        ),
      ),
      h(
        'div',
        { class: 'qa-modal__body' },
        activeTab === 'pages'
          ? h(
              'ul',
              { class: 'qa-tree' },
              tree.map((row) => {
                const isHere = here !== null && row.node.path_pattern === here
                return h(
                  'li',
                  {
                    key: row.node.id,
                    class: `qa-tree__row ${isHere ? 'qa-tree__row--here' : ''}`,
                    'data-testid': isHere ? 'qa-tree-current' : 'qa-sitemap-node',
                    style: { paddingLeft: `${0.5 + row.depth * 1.1}rem` },
                  },
                  h(
                    'span',
                    {
                      class: `qa-dot ${QA_COLOR_MODIFIER[row.node.color]}`,
                      'aria-hidden': 'true',
                    },
                  ),
                  h(
                    'span',
                    {
                      class: `qa-tree__title ${row.node.structural ? 'qa-tree__title--structural' : ''}`,
                    },
                    row.node.title || row.node.path_pattern,
                  ),
                  isHere ? h('span', { class: 'qa-tree__here' }, ` ${row.node.path_pattern} `) : null,
                  isHere ? h('span', { class: 'qa-tree__here' }, strings['modal.youAreHere']) : null,
                  h('span', { class: 'qa-tree__count' }, String(row.node.counts.scenarios)),
                )
              }),
            )
          : h(
              'div',
              { class: 'qa-scenarios' },
              h('input', {
                type: 'search',
                class: 'qa-scenarios__search',
                placeholder: strings['modal.search'],
                'data-testid': 'qa-scenarios-search',
                value: search,
                onInput: (e: Event) => setSearch((e.target as HTMLInputElement).value),
              }),
              filteredCases.length === 0
                ? h('p', { class: 'qa-scenarios__empty' }, strings['modal.noMatch'])
                : h(
                    'ul',
                    { class: 'qa-scenarios__list' },
                    filteredCases.map((entry) => {
                      const scenario = entry.scenario
                      const lanes = {
                        dev: humanTriColor(scenario.dev),
                        bot: botTriColor(scenario.automated),
                        human: humanTriColor(scenario.client),
                      }
                      const traversed = [...entry.traversed].map(patternTitle)
                      return h(
                        'li',
                        {
                          key: scenario.external_key ?? scenario.id,
                          class: 'qa-scenarios__item',
                          'data-testid': 'qa-scenario-row',
                        },
                        h(
                          'div',
                          { class: 'qa-scenarios__line' },
                          h('span', { class: `qa-dot ${QA_COLOR_MODIFIER[scenario.color]}`, 'aria-hidden': 'true' }),
                          scenario.group?.key
                            ? h('span', { class: 'qa-suitekey' }, scenario.group.key)
                            : null,
                          h('span', { class: 'qa-scenarios__title' }, scenario.title),
                          scenario.source === 'testgen'
                            ? h('span', { class: 'qa-tag' }, strings['source.testgen'])
                            : null,
                          h(LaneIcons, { dev: lanes.dev, bot: lanes.bot, human: lanes.human, strings }),
                        ),
                        h(
                          'div',
                          { class: 'qa-scenarios__pages' },
                          entry.primaries.size
                            ? h(
                                'span',
                                { class: 'qa-scenarios__primary' },
                                interpolate(strings['modal.appliesTo'], { count: entry.primaries.size }),
                              )
                            : null,
                          traversed.length
                            ? h(
                                'span',
                                { class: 'qa-scenarios__traversed' },
                                `${strings['modal.traversed']}: ${traversed.join(', ')}`,
                              )
                            : null,
                        ),
                      )
                    }),
                  ),
            ),
      ),
    ),
  )
}
